import React, { useMemo, type FC } from "react";
import { PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { Button, DataTable } from "../../../../components/shared";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import { useListTemplates } from "../../../../queries/dynamicBonuses/useListTemplates";
import {
  EMPTY_DISPLAY,
  LIST_TABLE_BANNER_COLUMN_WIDTH,
  LIST_TABLE_NAME_COLUMN_WIDTH,
} from "../constants";
import { formatListTableDateTime } from "../listTableDateTime";
import { triggerModeDisplayLabel } from "../triggerModeLabels";
import { inferTriggerModeFromStepSpecs } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import type { ResultSpecIn, TemplateOut } from "../types";
import { compareIsoDates, compareStrings } from "../listTableSorters";
import CampaignBuilderHeader from "../controls/CampaignBuilderHeader";
import OfferPreview from "../controls/OfferPreview";
import TemplateRowActions from "./TemplateRowActions";
import { DISPLAY_BANNER_RESULT_TYPE_KEY } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import listStyles from "../../../Affiliates/Affiliates.module.css";
import styles from "../stylesDynamicBonuses.module.css";

type TemplatePreview = {
  key: string;
  result?: ResultSpecIn;
  banner: TemplateOut["default_steps"][number]["banner"];
  layout: TemplateOut["default_steps"][number]["freeform_layout"];
};

const collectTemplatePreviews = (template: TemplateOut): TemplatePreview[] =>
  (template.default_steps ?? []).flatMap(
    (step, stepIndex): TemplatePreview[] => {
      const key = `${step.step_id}-${stepIndex}`;
      const displayBanner = step.results.find(
        (candidate) => candidate.type === DISPLAY_BANNER_RESULT_TYPE_KEY,
      );

      if (displayBanner || step.banner) {
        return [
          {
            key,
            result: displayBanner ?? step.results[0],
            banner: step.banner,
            layout: step.freeform_layout,
          },
        ];
      }

      const result = step.results.find((candidate) => {
        const params = candidate.params ?? {};
        return Boolean(params.banner_title_key || params.banner_msg_key);
      });
      if (!result) return [];

      return [
        {
          key,
          result,
          banner: step.banner,
          layout: step.freeform_layout,
        },
      ];
    },
  );

const templateTriggerType = (template: TemplateOut): string | null => {
  const steps = template.default_steps ?? [];
  if (steps.length === 0) return null;
  return triggerModeDisplayLabel(inferTriggerModeFromStepSpecs(steps));
};

const renderTemplatePreview = (template: TemplateOut): React.ReactNode => {
  const previews = collectTemplatePreviews(template);
  const [firstPreview] = previews;
  if (!firstPreview) {
    return (
      <div className={`${styles.campaignPreviewCell} ${styles.campaignPreviewEmpty}`}>
        {EMPTY_DISPLAY}
      </div>
    );
  }

  const remainingCount = previews.length - 1;

  return (
    <div className={styles.campaignPreviewCell}>
      <div className={styles.campaignPreviewBanner}>
        <OfferPreview
          result={firstPreview.result}
          banner={firstPreview.banner}
          layout={firstPreview.layout}
          localizedStrings={template.default_localized_strings}
        />
      </div>
      {remainingCount > 0 ? (
        <span
          className={styles.campaignPreviewCount}
          aria-label={`${remainingCount} more preview${remainingCount === 1 ? "" : "s"}`}
        >
          +{remainingCount}
        </span>
      ) : (
        <span
          className={`${styles.campaignPreviewCount} ${styles.campaignPreviewCountPlaceholder}`}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

// Order: Banner → Name → … → Created/Updated (rightmost). Duplicate sits in
// a trailing actions column when bonus.campaign_duplicate_builder is on.
const buildColumns = (showDuplicate: boolean): ColumnsType<TemplateOut> => {
  const columns: ColumnsType<TemplateOut> = [
    {
      title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_BANNER,
      key: "preview",
      width: LIST_TABLE_BANNER_COLUMN_WIDTH,
      ellipsis: false,
      className: styles.templatePreviewColumn,
      render: (_: unknown, record: TemplateOut) => renderTemplatePreview(record),
    },
    {
      title: AppStrings.NAME,
      dataIndex: "name",
      key: "name",
      width: LIST_TABLE_NAME_COLUMN_WIDTH,
      render: (name: string) => <span className={styles.templateName}>{name}</span>,
      sorter: (a, b) => compareStrings(a.name, b.name),
    },
    {
      title: AppStrings.PAM_TEMPLATES_TRIGGER_TYPE,
      key: "trigger_type",
      width: 160,
      render: (_: unknown, record: TemplateOut) =>
        templateTriggerType(record) ?? EMPTY_DISPLAY,
      sorter: (a, b) =>
        compareStrings(templateTriggerType(a), templateTriggerType(b)),
    },
    {
      title: AppStrings.PAM_TEMPLATES_CREATED,
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      render: (value: string) => formatListTableDateTime(value),
      sorter: (a, b) => compareIsoDates(a.created_at, b.created_at),
    },
    {
      title: AppStrings.PAM_TEMPLATES_UPDATED,
      dataIndex: "updated_at",
      key: "updated_at",
      width: 140,
      render: (value: string) => formatListTableDateTime(value),
      sorter: (a, b) => compareIsoDates(a.updated_at, b.updated_at),
    },
  ];

  if (showDuplicate) {
    columns.push({
      title: "",
      key: "actions",
      width: 56,
      ellipsis: false,
      render: (_: unknown, record: TemplateOut) => (
        <TemplateRowActions templateId={record.template_id} />
      ),
    });
  }

  return columns;
};

const TemplatesPage: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useListTemplates();
  const { isEnabled: duplicateBuilderEnabled } = usePamFeature(
    "dynamicBonusesDuplicateBuilder",
  );
  const columns = useMemo(
    () => buildColumns(Boolean(duplicateBuilderEnabled)),
    [duplicateBuilderEnabled],
  );

  const rows = useMemo<TemplateOut[]>(() => data ?? [], [data]);

  return (
    <>
      <FeatureFlagGuard feature="dynamicBonuses" mode="route">
        <div className={`${listStyles.container} ${styles.templatesContainer}`}>
          <CampaignBuilderHeader
            title={AppStrings.PAM_BONUS_TEMPLATES_TITLE}
            backAriaLabel={AppStrings.PAM_BACK_TO_CAMPAIGNS}
            onBack={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS)}
            actions={
              <Button
                intent="primary"
                iconLeft={<PlusOutlined />}
                className={styles.createTemplateButton}
                onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATE_NEW)}
              >
                {AppStrings.PAM_CREATE_TEMPLATE}
              </Button>
            }
          />

          <div className={styles.templatesTableSection}>
            {isError && (
              <p className={styles.listError}>{AppStrings.PAM_TEMPLATES_LOAD_ERROR}</p>
            )}

            <DataTable<TemplateOut>
              rowKey="template_id"
              columns={columns}
              dataSource={rows}
              loading={isLoading}
              pagination={false}
              className={styles.templatesTable}
              onRow={(record) => ({
                onClick: () =>
                  navigate(
                    NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATE_EDIT.replace(
                      ":templateId",
                      record.template_id,
                    ),
                  ),
                style: { cursor: "pointer" },
              })}
            />
          </div>
        </div>
      </FeatureFlagGuard>
    </>
  );
};

export default TemplatesPage;
